import {
  useReducer,
  useState,
  useSyncExternalStore,
  type JSX,
  type ReactNode,
} from 'react';

import { getRuntimeConfig, loadRuntimeConfig } from '@/lib/runtime-config.js';
import { getState, subscribe } from '@/store/identity-store.js';

import { MaintenanceSplash } from './MaintenanceSplash.js';

/**
 * MaintenanceGate — shows the downtime splash BEFORE login when maintenance is
 * on, so an unauthenticated visitor (browser or Teams Tab) sees the splash
 * instead of the sign-in screen. Reads the boot-loaded runtime config
 * synchronously, so it sits above the app providers (no query/router needed).
 *
 * Only gates the pre-login case: once a platform JWT is present, it falls through
 * to AppShell, whose own maintenance gate enforces allow-admins (admins keep the
 * app, others get the in-app downtime screen). "Try again" re-fetches /v1/config;
 * "Sign in" bypasses to the normal login.
 */
export function MaintenanceGate({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [bypassed, setBypassed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [, recheck] = useReducer((tick: number) => tick + 1, 0);

  const maintenance = getRuntimeConfig().maintenance;
  // Subscribe to the identity store so the gate reacts if the platform JWT
  // appears/clears (sign-in/out) rather than reading a one-shot snapshot.
  const signedIn = useSyncExternalStore(subscribe, () =>
    Boolean(getState().platformJwt),
  );
  if (!maintenance.enabled || bypassed || signedIn) {
    return <>{children}</>;
  }

  const onRetry = (): void => {
    if (retrying) return;
    setRetrying(true);
    void loadRuntimeConfig(true).finally(() => {
      setRetrying(false);
      recheck();
    });
  };

  return (
    <MaintenanceSplash
      maintenance={maintenance}
      retrying={retrying}
      onRetry={onRetry}
      onSignIn={() => setBypassed(true)}
    />
  );
}
